import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Activity, 
  Brain, 
  Droplets, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  FileText,
  RefreshCw,
  Info
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

const HRADashboard = ({ user, riskAssessment, onRetakeAssessment }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { apiCall } = useApp();

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      case 'moderate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'very_high': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return <CheckCircle className="w-5 h-5" />;
      case 'moderate': return <Info className="w-5 h-5" />;
      case 'high': return <AlertTriangle className="w-5 h-5" />;
      case 'very_high': return <AlertTriangle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      cardiovascular: <Heart className="w-5 h-5" />,
      diabetes: <Droplets className="w-5 h-5" />,
      hypertension: <Activity className="w-5 h-5" />,
      mental_health: <Brain className="w-5 h-5" />,
      obesity: <Activity className="w-5 h-5" />,
      cancer: <AlertTriangle className="w-5 h-5" />,
      respiratory: <Activity className="w-5 h-5" />,
      stroke: <Brain className="w-5 h-5" />
    };
    return icons[category] || <Activity className="w-5 h-5" />;
  };

  const formatCategoryName = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getOverallRiskMessage = (score, level) => {
    if (level === 'low') {
      return 'Your overall health risk is low. Keep up the good work with your healthy lifestyle!';
    } else if (level === 'moderate') {
      return 'Your health risk is moderate. There are opportunities to improve your health outcomes.';
    } else if (level === 'high') {
      return 'Your health risk is elevated. Consider discussing these results with your healthcare provider.';
    } else {
      return 'Your health risk is very high. We strongly recommend consulting with a healthcare professional.';
    }
  };

  const getPriorityRecommendations = () => {
    return riskAssessment.recommendations
      .filter(rec => rec.priority === 'high')
      .slice(0, 3);
  };

  const getTopRiskCategories = () => {
    return Object.entries(riskAssessment.individual_risks)
      .sort(([,a], [,b]) => b.risk_score - a.risk_score)
      .slice(0, 4);
  };

  if (!riskAssessment) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Risk Assessment Available</h2>
          <p className="text-gray-600 mb-6">Complete your Health Risk Assessment to see your personalized health insights.</p>
          <button
            onClick={onRetakeAssessment}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Start Health Assessment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Health Risk Assessment</h1>
          <p className="text-gray-600">
            Assessment completed on {new Date(riskAssessment.assessment_date).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={onRetakeAssessment}
          disabled={isLoading}
          className="flex items-center px-4 py-2 text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retake Assessment
        </button>
      </div>

      {/* Overall Risk Score */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Overall Health Risk</h2>
          <div className={`flex items-center px-3 py-1 rounded-full border ${getRiskColor(riskAssessment.risk_category)}`}>
            {getRiskIcon(riskAssessment.risk_category)}
            <span className="ml-2 font-medium capitalize">
              {riskAssessment.age_based_scoring?.risk_label || riskAssessment.risk_category.replace('_', ' ')}
            </span>
          </div>
        </div>
        
        {/* Age Group Info */}
        {riskAssessment.age_based_scoring && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Age Group:</span>
              <span className="font-medium text-gray-900 capitalize">
                {riskAssessment.age_based_scoring.age_group === 'below_25' ? 'Below 25' :
                 riskAssessment.age_based_scoring.age_group === '26_50' ? '26-50' :
                 riskAssessment.age_based_scoring.age_group === '51_above' ? '51+' :
                 riskAssessment.age_based_scoring.age_group}
              </span>
            </div>
            {riskAssessment.age_based_scoring.percentage_score !== undefined && (
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-600">Health Score:</span>
                <span className="font-medium text-gray-900">
                  {riskAssessment.age_based_scoring.percentage_score}%
                </span>
              </div>
            )}
          </div>
        )}
        
        <div className="flex items-center mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Risk Score</span>
              <span className="text-2xl font-bold text-gray-900">{riskAssessment.overall_risk_score}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ${
                  riskAssessment.overall_risk_score < 25 ? 'bg-green-500' :
                  riskAssessment.overall_risk_score < 50 ? 'bg-yellow-500' :
                  riskAssessment.overall_risk_score < 75 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                style={{ width: `${riskAssessment.overall_risk_score}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        <p className="text-gray-700">
          {getOverallRiskMessage(riskAssessment.overall_risk_score, riskAssessment.risk_category)}
        </p>
      </div>

      {/* Risk Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {getTopRiskCategories().map(([category, riskData]) => (
          <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${getRiskColor(riskData.risk_level)} border-0`}>
                  {getCategoryIcon(category)}
                </div>
                <div className="ml-3">
                  <h3 className="font-medium text-gray-900 text-sm">{formatCategoryName(category)}</h3>
                  <p className="text-xs text-gray-500 capitalize">{riskData.risk_level.replace('_', ' ')} Risk</p>
                </div>
              </div>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Score</span>
                <span className="text-sm font-semibold">{riskData.risk_score}/100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    riskData.risk_score < 25 ? 'bg-green-500' :
                    riskData.risk_score < 50 ? 'bg-yellow-500' :
                    riskData.risk_score < 75 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${riskData.risk_score}%` }}
                ></div>
              </div>
            </div>
            
            {riskData.contributing_factors && riskData.contributing_factors.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-600 mb-1">Key Factors:</p>
                <ul className="text-xs text-gray-700 space-y-1">
                  {riskData.contributing_factors.slice(0, 2).map((factor, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-1 h-1 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                      <span className="line-clamp-2">{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Age-Group Specific Recommendations */}
      {riskAssessment.age_based_scoring?.recommendations && riskAssessment.age_based_scoring.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            Age-Group Specific Recommendations
          </h2>
          <div className="space-y-3">
            {riskAssessment.age_based_scoring.recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                  {index + 1}
                </div>
                <p className="text-gray-800 flex-1">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Priority Recommendations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
          Priority Recommendations
        </h2>
        
        {getPriorityRecommendations().length > 0 ? (
          <div className="space-y-4">
            {getPriorityRecommendations().map((recommendation, index) => (
              <div key={index} className="flex items-start p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-1 capitalize">{recommendation.category}</p>
                  <p className="text-gray-700">{recommendation.text}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">Great job! You don't have any high-priority health recommendations at this time.</p>
        )}
      </div>

      {/* All Recommendations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Recommendations</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {riskAssessment.recommendations.map((recommendation, index) => (
            <div key={index} className={`p-4 rounded-lg border ${
              recommendation.priority === 'high' ? 'bg-red-50 border-red-200' :
              recommendation.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                  recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {recommendation.priority} priority
                </span>
                <span className="text-xs text-gray-500 capitalize">{recommendation.category}</span>
              </div>
              <p className="text-gray-800">{recommendation.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Assessment Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Important Information</p>
            <ul className="space-y-1 text-blue-700">
              <li>• This assessment is for informational purposes only and does not replace professional medical advice</li>
              <li>• Risk scores are estimates based on population data and your responses</li>
              <li>• Consider discussing these results with your healthcare provider</li>
              <li>• Assessment expires on {new Date(riskAssessment.expires_at).toLocaleDateString()}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRADashboard;
